import classNames from 'classnames';
import { Fragment, InputHTMLAttributes, ReactNode, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineSelector, HiX } from 'react-icons/hi';
import { usePopper } from 'react-popper';
import { Portal } from 'react-portal';

import { Combobox, Transition } from '@headlessui/react';

import AutocompleteItem from './autocomplete-item';
import AutocompleteNoOptionsPlaceholder from './autocomplete-no-options-placeholder';
import { AutocompleteModifiers } from './autocomplete.constants';

import { useFieldState, WithFieldStateProps } from '../../hooks';
import { Spinner } from '../spinner';

export type AutocompleteValue = InputHTMLAttributes<HTMLInputElement>['value'] | null;

export interface AutocompleteProps<T> extends WithFieldStateProps {
  className?: string;
  selected?: T;
  getSelectedLabel?: (option?: T) => string;
  getOptionLabel?: (option?: T) => string | ReactNode;
  options?: T[];
  onChange: (selected: T) => void;
  onClear?: () => void;
  onInput?: (value: string) => void;
  onOpen?: () => void;
  placeholder?: string;
  name?: string;
  required?: boolean;
  size?: 'medium' | 'small';
  loading?: boolean;
  renderOptions?: (options: T[] | undefined) => ReactNode | undefined;
}

const renderAutocompleteLabel = (label: ReactNode): ReactNode => {
  const LABEL_FALLBACK = '\xa0';
  if (label == null) return LABEL_FALLBACK;
  if (typeof label === 'string' && label.trim() === '') return LABEL_FALLBACK;
  return label;
};

/**
 * Autocomplete to enable autocomplete menus with options
 */
export function Autocomplete<T>(props: AutocompleteProps<T>) {
  const {
    className,
    selected,
    getSelectedLabel,
    getOptionLabel,
    options,
    onChange,
    onClear,
    onInput,
    onOpen,
    name,
    placeholder,
    readOnly,
    disabled,
    size = 'medium',
    loading,
    renderOptions,
  } = props;

  const { t: tCommon } = useTranslation('common');

  const valueLabel = useMemo(() => {
    if (typeof getSelectedLabel !== 'function') return selected as unknown as AutocompleteValue;
    if (selected == null) return undefined;
    const valueLabel = getSelectedLabel(selected);
    if (valueLabel == null) return undefined;
    return valueLabel;
  }, [getSelectedLabel, selected]);

  const fieldState = useFieldState(props);

  const hasLoadingIndicator = loading;
  const hasClearButton = !hasLoadingIndicator && !!valueLabel && !readOnly && !disabled;
  const hasSelectorIndicator = !hasLoadingIndicator && !hasClearButton && !readOnly;

  const comboboxButtonRef = useRef<HTMLButtonElement>(null);

  /**
   * Popper setup
   */
  const popperElRef = useRef(null);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    modifiers: AutocompleteModifiers,
  });

  /**
   * --------------
   */

  return (
    <Combobox value={selected} name={name} disabled={disabled} onChange={onChange}>
      {({ open }) => (
        <>
          <div
            ref={(ref: any) => setReferenceElement(ref)} // eslint-disable-line @typescript-eslint/no-explicit-any
            className={classNames(
              'relative w-full text-left text-white bg-surface-elevated rounded-md shadow-sm focus:outline-none',
              size === 'medium' && 'pl-3 pb-2 pt-2 pr-10',
              size === 'small' && 'px-2 py-1 pt-1 pr-6',
              fieldState !== 'disabled' &&
                fieldState !== 'disabledError' &&
                fieldState !== 'disabledDirty' &&
                'cursor-default',
              fieldState !== 'readOnly' && 'bg-surface-elevated dark:bg-neutral-700',
              fieldState === 'readOnly' &&
                'border-neutral-50 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900',
              fieldState === 'disabled' && 'border-gray-200 dark:border-neutral-600 cursor-not-allowed',
              fieldState === 'disabledError' && 'border-red-300 dark:border-red-200 cursor-not-allowed',
              fieldState === 'disabledDirty' && 'border-sky-300 dark:border-sky-200 cursor-not-allowed',
              fieldState === 'submitting' && 'animate-pulse border-gray-200 dark:border-neutral-600',
              fieldState === 'submittingDirty' && 'animate-pulse border-sky-300 dark:border-sky-200',
              fieldState === 'default' &&
                'border-gray-300 dark:border-neutral-800 focus-visible:ring-brand-primary focus-visible:border-brand-primary focus-visible:dark:ring-cyan-500 focus-visible:dark:border-cyan-500 focus-visible:ring-offset-0 focus-visible:ring-1',
              fieldState === 'default' &&
                'focus-visible:ring-brand-primary focus-visible:border-brand-primary focus-visible:dark:ring-cyan-500 focus-visible:dark:border-cyan-500 focus-visible:ring-offset-0 focus-visible:ring-1',
              open &&
                fieldState === 'default' &&
                'ring-brand-primary border-brand-primary dark:ring-cyan-400 dark:border-cyan-400 ring-offset-0 ring-1',
              fieldState === 'dirty' && 'border-sky-400 dark:border-sky-300',
              fieldState === 'dirty' &&
                'focus-visible:ring-brand-primary focus-visible:border-brand-primary focus-visible:dark:ring-cyan-500 focus-visible:dark:border-cyan-500 focus-visible:ring-offset-0 focus-visible:ring-1',
              open &&
                fieldState === 'dirty' &&
                'ring-brand-primary border-brand-primary dark:ring-cyan-400 dark:border-cyan-400 ring-offset-0 ring-1',
              fieldState === 'error' && 'border-red-500 dark:border-red-400',
              fieldState === 'error' &&
                'focus-visible:ring-red-500 focus-visible:border-red-500 focus-visible:dark:ring-red-400 focus-visible:dark:border-red-400 focus-visible:ring-offset-0 focus-visible:ring-1',
              open &&
                fieldState === 'error' &&
                'ring-red-500 border-red-500 dark:ring-red-400 dark:border-red-400 ring-offset-0 ring-1',
              className,
            )}
          >
            {/* Input  */}
            <Combobox.Input
              readOnly={readOnly || disabled}
              className={classNames(
                'block w-full border-0 border-hidden appearance-none focus:outline-none truncate bg-surface-elevated disabled:bg-surface-elevated',
                size === 'medium' && 'sm:text-sm text-base',
                size === 'small' && 'text-xs leading-[normal]',
                fieldState === 'disabled' && 'opacity-60 cursor-not-allowed',
                fieldState === 'disabledError' && 'opacity-60 cursor-not-allowed',
                fieldState === 'disabledDirty' && 'opacity-60 cursor-not-allowed',
                fieldState === 'submitting' && 'opacity-60',
                fieldState === 'submittingDirty' && 'opacity-60',
                fieldState === 'readOnly' && 'cursor-default bg-inherit',
              )}
              onClick={() => !open && comboboxButtonRef.current?.click()}
              onChange={(event) => onInput?.(event.target.value)}
              placeholder={placeholder}
              displayValue={(x: T) => getSelectedLabel?.(x) ?? ''}
              autoComplete="off"
              data-1p-ignore
            />

            {/* Indicators & Buttons */}
            <div
              className={classNames(
                'absolute inset-y-0 right-0 flex items-center pr-1',
                disabled && 'pointer-events-none opacity-40',
              )}
            >
              {/* Loading Indicator */}
              <div
                className={classNames(
                  'flex items-center',
                  size === 'medium' && 'p-1.5',
                  size === 'small' && 'p-0.5',
                  !hasLoadingIndicator && 'hidden',
                )}
              >
                <Spinner size="sm" />
              </div>

              {/* Selector Indicator Button */}
              <Combobox.Button
                ref={comboboxButtonRef}
                className={classNames(
                  'flex items-center rounded-full text-gray-400 hover:text-secondary hover:bg-gray-100 transition ease-in-out duration-150',
                  size === 'medium' && 'p-1.5',
                  size === 'small' && 'p-0.5',
                  !hasSelectorIndicator && 'hidden',
                )}
                onClick={() => {
                  onOpen?.();
                }}
              >
                <HiOutlineSelector
                  className={classNames(size === 'medium' && 'h-5 w-5', size === 'small' && 'h-3 w-3')}
                />
              </Combobox.Button>

              {/* Clear Button */}
              <div
                className={classNames(
                  'cursor-pointer flex items-center rounded-full text-gray-400 hover:text-secondary hover:bg-gray-100 transition ease-in-out duration-150',
                  size === 'medium' && 'p-1.5',
                  size === 'small' && 'p-0.5',
                  !hasClearButton && 'hidden',
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeof onClear === 'function') {
                    onClear();
                    return;
                  }
                  onChange(null as any); // eslint-disable-line @typescript-eslint/no-explicit-any
                }}
              >
                <HiX />
              </div>
            </div>
          </div>

          <Portal>
            <div ref={popperElRef} style={styles['popper']} className="z-40" {...attributes['popper']}>
              <Transition
                as={Fragment}
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
                beforeEnter={() => setPopperElement(popperElRef.current)}
                afterLeave={() => setPopperElement(null)}
              >
                <Combobox.Options
                  static
                  className="max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                >
                  {typeof renderOptions === 'function' ? (
                    renderOptions(options)
                  ) : !Array.isArray(options) || options?.length <= 0 ? (
                    <AutocompleteNoOptionsPlaceholder>{tCommon('nothingFound')}</AutocompleteNoOptionsPlaceholder>
                  ) : (
                    options?.map((option, idx) => (
                      <Combobox.Option key={idx} value={option}>
                        {({ selected, active }) => (
                          <AutocompleteItem selected={selected} active={active}>
                            {renderAutocompleteLabel(
                              typeof getOptionLabel === 'function'
                                ? getOptionLabel(option)
                                : (option as unknown as AutocompleteValue),
                            )}
                          </AutocompleteItem>
                        )}
                      </Combobox.Option>
                    ))
                  )}
                </Combobox.Options>
              </Transition>
            </div>
          </Portal>
        </>
      )}
    </Combobox>
  );
}

export default Autocomplete;
